import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	noValidation,
} from '@post-office/placement-common-v2';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { useHandleDismiss } from '@post-office/message-templates-common--growth-starburst/hooks/use-handle-dismiss';
import { type MessageTemplateContext } from '@post-office/message-template--twc-sidenav-banner';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { GenericBanner } from '../../../components/generic-banner';
import { PersonalizedBanner } from '../../../components/personalized-banner';
import { type RecommendedApp } from '../../../index';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import {
	getAtlassianHomeUrl,
	getLoomOnboardingUrl,
	getOnboardingUrl,
	resolveRedirectTo,
} from '../../../utils/get-onboarding-url';
import { HydrationAbortError } from '@post-office/errors';

const MISSING_APP_EXPERIMENT = 'twcg_missing_app_activation_banner';

type ExperimentArm = 'control' | 'generic' | 'personalized';

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onboardingUrl: string;
	messageInstanceId: string;
	locale: string;
	redirectTo: 'jira' | 'confluence';
	arm: ExperimentArm;
	experimentKey?: string;
	targetTenantId?: string;
	collectionEntitlementId?: string;
};

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: noValidation(),
		stage: () => ({
			goalMetadata: {
				primaryGoal: {
					goalId: 'clickthrough',
				},
			},
		}),
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: async ({ message, request }) => {
			const { logger, orchestrationContext, statsig } = request;
			const { messageInstanceId, messageTemplateId, triggerId } = message;
			const { cloudId: tenantId } = message.context;
			const { locale } = orchestrationContext;

			const {
				recommendedApp,
				actionTier,
				targetTenantId,
				collectionEntitlementId,
				orgId,
				twcEntitlementId,
			} = message.context;

			if (!tenantId || typeof tenantId !== 'string') {
				logger.error(
					{ messageInstanceId, messageTemplateId, triggerId },
					'Tenant ID not available',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', { reason: 'tenant-id-not-available' })
					.incr();
				throw new Error('Tenant ID not available');
			}

			const armValue = statsig.getExperimentValue(
				{ identifiers: { tenantId } },
				{ name: MISSING_APP_EXPERIMENT, parameter: 'arm', defaultValue: 'control' },
			);
			const arm: ExperimentArm =
				armValue === 'generic' || armValue === 'personalized' ? armValue : 'control';

			const env = request.executionRuntime.getEnvironmentType();
			const homeUrl = () => getAtlassianHomeUrl(env);

			const commonReturn = {
				...message.context,
				arm,
				recommendedApp,
				actionTier,
				targetTenantId,
				collectionEntitlementId,
				messageInstanceId,
				locale,
			};

			switch (arm) {
				case 'control': {
					logger.info(
						{ messageInstanceId, messageTemplateId, triggerId, arm },
						'Control arm — aborting hydration',
					);
					request.metrics
						.counter('twc-sidenav-banner.conf.info.count', { reason: 'control-arm' })
						.incr();
					throw new HydrationAbortError('Control cohort - abort hydration');
				}

				case 'generic': {
					const redirectTo = resolveRedirectTo(message.context.redirectTo);
					let onboardingUrl = await getOnboardingUrl({
						redirectTo,
						requestContext: request,
						messageInstanceId,
						messageTemplateId,
						orgId: orgId as string | undefined,
						twcEntitlementId: twcEntitlementId as string | undefined,
					});

					if (!onboardingUrl) {
						onboardingUrl = homeUrl();
						logger.info(
							{ messageTemplateId, messageInstanceId, triggerId, arm },
							'No onboarding URL found for generic arm — defaulting to Atlassian Home',
						);
						request.metrics
							.counter('twc-sidenav-banner.conf.info.count', {
								reason: 'generic-defaulted-to-atlassian-home',
							})
							.incr();
					}

					request.metrics.counter('twc-sidenav-banner.conf.success.count', { arm }).incr();

					return { ...commonReturn, redirectTo, onboardingUrl };
				}

				case 'personalized': {
					const isLoomPersonalized = recommendedApp === 'loom';
					const redirectTo: 'jira' | 'confluence' =
						recommendedApp === 'confluence' ? 'confluence' : 'jira';

					let onboardingUrl: string | undefined;

					if (isLoomPersonalized) {
						onboardingUrl = getLoomOnboardingUrl(env);
					} else {
						onboardingUrl = await getOnboardingUrl({
							redirectTo,
							requestContext: request,
							messageInstanceId,
							messageTemplateId,
							orgId: orgId as string | undefined,
							twcEntitlementId: twcEntitlementId as string | undefined,
						});
					}

					if (!onboardingUrl) {
						onboardingUrl = homeUrl();
						logger.info(
							{ messageTemplateId, messageInstanceId, triggerId, recommendedApp },
							'No onboarding URL found for personalized arm — defaulting to Atlassian Home',
						);
						request.metrics
							.counter('twc-sidenav-banner.conf.info.count', {
								reason: 'personalized-defaulted-to-atlassian-home',
							})
							.incr();
					}

					request.metrics.counter('twc-sidenav-banner.conf.success.count', { arm }).incr();

					return { ...commonReturn, redirectTo, onboardingUrl };
				}
			}
		},
	},
}));

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({
			handleOnDismissed,
			onboardingUrl,
			messageInstanceId,
			locale,
			arm,
			redirectTo,
			recommendedApp,
			experimentKey,
			actionTier,
			targetTenantId,
			collectionEntitlementId,
		}: MessageFrontendProps) => {
			const { handleDismiss } = useHandleDismiss({
				onDismissed: handleOnDismissed,
				clickIdentifier:
					arm === 'personalized'
						? 'twc-sidenav-banner-personalized-dismiss'
						: arm === 'generic'
							? 'twc-sidenav-banner-generic-dismiss'
							: 'twc-sidenav-banner-dismiss',
			});

			if (arm === 'personalized' && recommendedApp && actionTier) {
				return (
					<PersonalizedBanner
						locale={locale}
						recommendedApp={recommendedApp as RecommendedApp}
						onClose={handleDismiss}
						onCtaClick={handleDismiss}
						href={onboardingUrl}
						messageInstanceId={messageInstanceId}
						experimentKey={experimentKey}
						actionTier={actionTier}
						targetTenantId={targetTenantId}
						collectionEntitlementId={collectionEntitlementId}
						surfaceProduct="confluence"
					/>
				);
			}

			if (arm === 'generic') {
				return (
					<GenericBanner
						locale={locale}
						product={redirectTo}
						onClose={handleDismiss}
						onCtaClick={handleDismiss}
						href={onboardingUrl}
						messageInstanceId={messageInstanceId}
						surfaceProduct="confluence"
					/>
				);
			}

			return null;
		},
	},
});
